import { useMemo, useState } from "react";
import { MultiSelectDropdown } from "../../digital-signature/components/MultiSelectDropdown";
import { getNodeIcon, getVariableIcon, type NodeVariable } from "../../types";

interface FileVariableSelectorProps {
  variables: NodeVariable[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
}

/**
 * Selector múltiple de variables para archivos adjuntos
 * Filtra y muestra solo variables de tipo file con checkboxes
 */
export const FileVariableSelector: React.FC<FileVariableSelectorProps> = ({
  variables,
  selectedValues,
  onChange,
}) => {
  const [variableSearch, setVariableSearch] = useState("");

  const variableGroups = useMemo(() => {
    // Para archivos adjuntos aceptamos variables de tipo file
    const allowedTypes = ["file"];

    return variables
      .map((nodeVar) => {
        // Filter file type variables
        const fileVariables = nodeVar.variables.filter((v) =>
          allowedTypes.includes(v.type),
        );

        if (fileVariables.length === 0) return null;

        const isSignDocumentNode = nodeVar.type === "sign-document-node";

        return {
          groupId: nodeVar.nodeId,
          groupLabels: [nodeVar.name],
          icon: getNodeIcon(nodeVar.type),
          items: fileVariables
            .filter((v) => {
              return (
                v.name.toLowerCase().includes(variableSearch.toLowerCase()) ||
                nodeVar.name
                  .toLowerCase()
                  .includes(variableSearch.toLowerCase())
              );
            })
            .map((v) => ({
              label: v.name.length > 45 ? `${v.name.slice(0, 45)}...` : v.name,
              value: isSignDocumentNode ? v.node_id || v.id : v.id,
              detail: v.type,
              type: v.type,
              icon: getVariableIcon(v.type),
            })),
        };
      })
      .filter(
        (group): group is NonNullable<typeof group> =>
          group !== null && group.items.length > 0,
      );
  }, [variables, variableSearch]);

  return (
    <MultiSelectDropdown
      options={variableGroups}
      selectedItems={selectedValues}
      onSelectionChange={onChange}
      placeholder="Elige un archivo..."
      search={{
        placeholder: "Buscar...",
        query: variableSearch,
        onSearch: (query) => {
          setVariableSearch(query);
        },
      }}
    />
  );
};
